/**
 * Acciones de producto compartidas por la product card, "Completá tu rutina"
 * (carrito y PDP) y el quick view.
 *
 *  - [data-tcl-add="<variant id>"]: agrega 1 unidad con feedback en el botón
 *    (cargando → ✓) y después abre el carrito.
 *  - [data-tcl-quick-view="<url del producto>"]: abre el quick view para
 *    elegir variante. Los datos salen de sections/tcl-product-data.liquid.
 *  - [data-tcl-cart-toggle]: ícono del carrito del header; abre el carrito Vue
 *    y mantiene actualizado su contador [data-tcl-cart-count].
 *
 * Expone window.TCLProduct para product-recs.js.
 */
(() => {
  if (window.TCLProduct) return;

  const T = Object.assign(
    {
      add: 'Agregar al carrito',
      soldOut: 'Agotado',
      details: 'Ver detalle del producto',
      close: 'Cerrar',
      cart: 'Carrito',
      error: 'No se pudo agregar. Probá de nuevo.',
    },
    window.TCL_PRODUCT_STRINGS
  );

  const escapeHtml = (str) =>
    String(str ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const money = (cents) => {
    if (window.$Cart?.convertCurrency) return window.$Cart.convertCurrency(cents);
    return new Intl.NumberFormat(document.documentElement.lang || 'es', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'ARS',
      maximumFractionDigits: 0,
    }).format(cents / 100);
  };

  const discountPct = (price, compareAt) => (compareAt > price ? Math.round(((compareAt - price) * 100) / compareAt) : 0);

  const priceHtml = (price, compareAt, { from = false } = {}) => {
    const pct = discountPct(price, compareAt);
    return `
      <span class="tcl-price__current">${from ? 'Desde ' : ''}${escapeHtml(money(price))}</span>
      ${pct ? `<s class="tcl-price__compare">${escapeHtml(money(compareAt))}</s><span class="tcl-tag">-${pct}%</span>` : ''}`;
  };

  // --- Carrito --------------------------------------------------------------

  const openCart = () => {
    if (typeof window.$Cart?.toggleDrawer === 'function') window.$Cart.toggleDrawer(true);
  };

  async function addToCart(variantId, { quantity = 1, properties = {} } = {}) {
    const cleanProps = Object.fromEntries(Object.entries(properties).filter(([, value]) => value));
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity, properties: cleanProps }] }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || data.message || T.error);
    if (typeof window.$Cart?.get === 'function') await window.$Cart.get();
    return data;
  }

  // Estados del botón: is-loading → is-added (✓) o is-error.
  async function withFeedback(button, action) {
    if (button.classList.contains('is-loading')) return false;
    button.classList.remove('is-added', 'is-error');
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    try {
      await action();
      button.classList.replace('is-loading', 'is-added');
      setTimeout(() => button.classList.remove('is-added'), 1800);
      return true;
    } catch (e) {
      console.error('[product-actions]', e);
      button.classList.replace('is-loading', 'is-error');
      button.title = e.message || T.error;
      setTimeout(() => button.classList.remove('is-error'), 1600);
      return false;
    } finally {
      button.removeAttribute('aria-busy');
    }
  }

  // Agregar rápido: se ve el ✓ y después se abre el carrito.
  async function quickAdd(button) {
    const ok = await withFeedback(button, () =>
      addToCart(button.dataset.tclAdd, { properties: { message: button.dataset.message } })
    );
    if (ok) setTimeout(openCart, 550);
  }

  // --- Quick view -----------------------------------------------------------

  const productCache = new Map();

  async function fetchProduct(url) {
    const base = url.split('?')[0];
    if (!productCache.has(base)) {
      productCache.set(
        base,
        fetch(`${base}?section_id=tcl-product-data`)
          .then((res) => {
            if (!res.ok) throw new Error(res.statusText);
            return res.text();
          })
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            return JSON.parse(doc.querySelector('[data-tcl-product]').textContent);
          })
          .catch((e) => {
            productCache.delete(base);
            throw e;
          })
      );
    }
    return productCache.get(base);
  }

  const quickView = {
    el: null,
    opener: null,
    product: null,
    selected: [],

    build() {
      if (this.el) return;
      this.el = document.createElement('div');
      this.el.className = 'tcl-qv';
      this.el.hidden = true;
      this.el.innerHTML = `
        <span class="tcl-qv__overlay" data-qv-close></span>
        <div class="tcl-qv__panel" role="dialog" aria-modal="true" tabindex="-1">
          <button type="button" class="tcl-qv__close" data-qv-close aria-label="${escapeHtml(T.close)}">
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19" stroke="currentColor" stroke-width="1.5" fill="none"/></svg>
          </button>
          <div class="tcl-qv__body"></div>
        </div>`;
      document.body.appendChild(this.el);
      this.el.addEventListener('click', (e) => {
        // Afuera del panel (la capa o el contenedor) o el botón de cerrar.
        if (e.target === this.el || e.target.closest('[data-qv-close]')) this.close();
        const value = e.target.closest('[data-qv-value]');
        if (value) this.select(Number(value.dataset.qvOption), value.dataset.qvValue);
        const add = e.target.closest('[data-qv-add]');
        if (add) this.add(add);
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !this.el.hidden) this.close();
      });
    },

    async open(url, opener) {
      this.build();
      this.opener = opener || null;
      this.el.querySelector('.tcl-qv__body').innerHTML = '<div class="tcl-qv__loading" aria-hidden="true"></div>';
      this.el.hidden = false;
      document.body.classList.add('tcl-qv-open');
      requestAnimationFrame(() => this.el.classList.add('is-open'));
      this.el.querySelector('.tcl-qv__panel').focus();

      try {
        this.product = await fetchProduct(url);
      } catch (e) {
        console.error('[quick-view]', e);
        window.location.href = url;
        return;
      }
      const first = this.product.variants.find((v) => v.available) || this.product.variants[0];
      this.selected = [...first.options];
      this.render();
    },

    close() {
      if (!this.el || this.el.hidden) return;
      this.el.classList.remove('is-open');
      document.body.classList.remove('tcl-qv-open');
      setTimeout(() => {
        this.el.hidden = true;
      }, 250);
      this.opener?.focus?.();
    },

    variant() {
      return this.product.variants.find((v) => v.options.every((value, i) => value === this.selected[i]));
    },

    // ¿Hay stock de este valor combinado con el resto de lo elegido?
    isAvailable(optionIndex, value) {
      return this.product.variants.some(
        (v) => v.available && v.options[optionIndex] === value && v.options.every((o, i) => i === optionIndex || o === this.selected[i])
      );
    },

    select(optionIndex, value) {
      this.selected[optionIndex] = value;
      this.render();
    },

    render() {
      const p = this.product;
      const v = this.variant();
      const hasOptions = !(p.options.length === 1 && p.variants.length === 1);
      const image = v?.image || p.image;

      const options = hasOptions
        ? p.options
            .map(
              (option, i) => `
              <fieldset class="tcl-qv__option">
                <legend>${escapeHtml(option.name)}: <span>${escapeHtml(this.selected[i])}</span></legend>
                <div class="tcl-qv__values">
                  ${option.values
                    .map(
                      (value) => `
                      <button type="button" class="tcl-qv__value${value === this.selected[i] ? ' is-selected' : ''}${this.isAvailable(i, value) ? '' : ' is-unavailable'}"
                        data-qv-option="${i}" data-qv-value="${escapeHtml(value)}" aria-pressed="${value === this.selected[i]}">${escapeHtml(value)}</button>`
                    )
                    .join('')}
                </div>
              </fieldset>`
            )
            .join('')
        : '';

      const canAdd = v && v.available;
      this.el.querySelector('.tcl-qv__panel').setAttribute('aria-label', p.title);
      this.el.querySelector('.tcl-qv__body').innerHTML = `
        <div class="tcl-qv__media">${image ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(p.title)}" width="600" height="600">` : ''}</div>
        <div class="tcl-qv__info">
          <h2 class="tcl-qv__title"><a href="${escapeHtml(p.url)}">${escapeHtml(p.title)}</a></h2>
          <div class="tcl-price">${priceHtml(v ? v.price : p.price, v ? v.compareAtPrice : p.compareAtPrice)}</div>
          ${options}
          <button type="button" class="tcl-qv__add button button--full-width" data-qv-add ${canAdd ? '' : 'disabled'}>
            <span>${canAdd ? escapeHtml(T.add) : escapeHtml(T.soldOut)}</span>
            <i class="tcl-qv__state" aria-hidden="true"></i>
          </button>
          <a class="tcl-qv__details" href="${escapeHtml(p.url)}">${escapeHtml(T.details)}</a>
        </div>`;
    },

    async add(button) {
      const v = this.variant();
      if (!v || !v.available) return;
      const ok = await withFeedback(button, () => addToCart(v.id, { properties: { message: this.product.message } }));
      if (ok) {
        setTimeout(() => {
          this.close();
          openCart();
        }, 550);
      }
    },
  };

  // --- Delegación de clicks -------------------------------------------------

  document.addEventListener('click', (e) => {
    const add = e.target.closest('[data-tcl-add]');
    if (add) {
      e.preventDefault();
      e.stopPropagation();
      quickAdd(add);
      return;
    }
    const view = e.target.closest('[data-tcl-quick-view]');
    if (view) {
      e.preventDefault();
      e.stopPropagation();
      quickView.open(view.dataset.tclQuickView, view);
    }
  });

  // --- Ícono del carrito del header ----------------------------------------

  // En captura: corta el evento antes de que llegue al listener que
  // cart-drawer.js (carrito nativo de Dawn) le engancha a #cart-icon-bubble,
  // así abre el carrito Vue (el que usa todo el sitio).
  const toggleVueCart = (e) => {
    const toggle = e.target.closest?.('[data-tcl-cart-toggle]');
    if (!toggle || !window.$Cart?.state?.settings?.isEnabled || typeof window.$Cart.toggleDrawer !== 'function') return;
    if (e.type === 'keydown' && e.code !== 'Space') return;
    e.preventDefault();
    e.stopPropagation();
    window.$Cart.toggleDrawer();
  };
  document.addEventListener('click', toggleVueCart, true);
  document.addEventListener('keydown', toggleVueCart, true);

  let lastCount = null;
  window.TCLCartWatch?.subscribe((cart) => {
    const count = cart.item_count;
    document.querySelectorAll('[data-tcl-cart-count]').forEach((bubble) => {
      bubble.hidden = !count;
      bubble.textContent = count < 100 ? count : '99+';
      if (lastCount !== null && count > lastCount) {
        bubble.classList.remove('is-bumped');
        void bubble.offsetWidth;
        bubble.classList.add('is-bumped');
      }
    });
    document.querySelectorAll('[data-tcl-cart-toggle]').forEach((link) =>
      link.setAttribute('aria-label', count ? `${T.cart} (${count})` : T.cart)
    );
    lastCount = count;
  });

  // --- Imágenes del buscador del header -------------------------------------
  // Las cards del buscador tienen loading="lazy" y viven dentro de un <details>
  // cerrado; en algunos navegadores no se cargan al abrirlo. Al abrir el
  // buscador (y cuando llegan resultados) se fuerza su carga.

  const eagerImages = (root) =>
    root.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      img.loading = 'eager';
    });

  document.addEventListener(
    'toggle',
    (e) => {
      const details = e.target;
      if (!details.open || !details.closest?.('.header__search')) return;
      eagerImages(details);
      if (!details.dataset.tclEager) {
        details.dataset.tclEager = 'true';
        new MutationObserver(() => details.open && eagerImages(details)).observe(details, { childList: true, subtree: true });
      }
    },
    true
  );

  window.TCLProduct = { addToCart, withFeedback, quickAdd, quickView, money, priceHtml, escapeHtml, discountPct };
})();
